import { RotateCw, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { formatDateTime } from '~/utils/date';

import type {
  PendingActivity,
  WorkflowExecutionDetail,
} from '../../types/workflow-inspection.types';
import { FailureChain } from '../failure-chain';
import { JsonViewer } from '../json-viewer';

/**
 * Pending Activities (from describe): every activity Temporal has scheduled or
 * running, with attempt-of-max, timings, last worker and the last failure —
 * an `attempt > 1` means it's retrying. Also flags a workflow task stuck on
 * repeated attempts (the workflow code or its worker failing).
 */
export function PendingActivitiesTab({ detail }: { detail: WorkflowExecutionDetail }) {
  const { t } = useTranslation('workflow-inspection');
  const task = detail.pendingWorkflowTask;

  return (
    <div className="space-y-3" data-testid="workflow-inspector-pending-activities">
      {task?.attempt !== null && task?.attempt !== undefined && task.attempt > 1 && (
        <p
          className="bg-warning/10 text-foreground flex items-center gap-2 rounded-md px-3 py-2 text-sm"
          data-testid="workflow-inspector-pending-workflow-task-failing-banner"
        >
          <TriangleAlert aria-hidden="true" className="text-warning size-4 shrink-0" />
          {t('pending.workflowTaskFailing', { attempt: task.attempt })}
        </p>
      )}
      {detail.pendingActivities.length === 0 ? (
        <p
          className="text-muted-foreground rounded-lg border p-6 text-center text-sm"
          data-testid="workflow-inspector-pending-activities-empty"
        >
          {t('pending.empty')}
        </p>
      ) : (
        detail.pendingActivities.map((a) => <PendingActivityCard key={a.activityId} activity={a} />)
      )}
    </div>
  );
}

function PendingActivityCard({ activity }: { activity: PendingActivity }) {
  const { t } = useTranslation('workflow-inspection');
  const retrying = (activity.attempt ?? 0) > 1;
  const attempt =
    activity.attempt === null
      ? null
      : activity.maximumAttempts
        ? t('pending.attemptOf', { attempt: activity.attempt, max: activity.maximumAttempts })
        : String(activity.attempt);
  const rows: { label: string; value: string | null }[] = [
    { label: t('pending.state'), value: activity.state },
    { label: t('pending.attempt'), value: attempt },
    {
      label: t('pending.scheduled'),
      value: activity.scheduledTime ? formatDateTime(activity.scheduledTime) : null,
    },
    {
      label: t('pending.lastStarted'),
      value: activity.lastStartedTime ? formatDateTime(activity.lastStartedTime) : null,
    },
    {
      label: t('pending.lastHeartbeat'),
      value: activity.lastHeartbeatTime ? formatDateTime(activity.lastHeartbeatTime) : null,
    },
    {
      label: t('pending.nextAttempt'),
      value: activity.nextAttemptScheduleTime
        ? formatDateTime(activity.nextAttemptScheduleTime)
        : null,
    },
    {
      label: t('pending.expires'),
      value: activity.expirationTime ? formatDateTime(activity.expirationTime) : null,
    },
    { label: t('pending.lastWorker'), value: activity.lastWorkerIdentity },
  ];

  return (
    <article
      className="bg-background space-y-3 rounded-lg border p-4"
      data-testid={`workflow-inspector-pending-activity-card-${activity.activityId}`}
    >
      <header className="flex flex-wrap items-center gap-2">
        <h4 className="text-foreground font-mono text-sm font-semibold">
          {activity.activityType ?? activity.activityId}
        </h4>
        <span className="text-muted-foreground font-mono text-xs">#{activity.activityId}</span>
        {retrying && (
          <Badge
            variant="outline"
            className="border-warning text-warning gap-1"
            data-testid="workflow-inspector-pending-activity-retrying-badge"
          >
            <RotateCw aria-hidden="true" className="size-3" />
            {t('pending.retrying')}
          </Badge>
        )}
      </header>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
        {rows
          .filter((r) => r.value)
          .map((r) => (
            <Field key={r.label} label={r.label}>
              {r.value}
            </Field>
          ))}
      </dl>
      {activity.lastFailure && (
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs">{t('pending.lastFailure')}</p>
          <FailureChain failure={activity.lastFailure} />
        </div>
      )}
      {activity.heartbeatDetails !== null && activity.heartbeatDetails !== undefined && (
        <div className="space-y-1">
          <p className="text-muted-foreground text-xs">{t('pending.heartbeatDetails')}</p>
          <JsonViewer
            value={activity.heartbeatDetails}
            title={t('pending.heartbeatDetails')}
            testId="workflow-inspector-pending-activity-heartbeat-json"
          />
        </div>
      )}
    </article>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-foreground break-all">{children}</dd>
    </div>
  );
}
